/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import apiService from '../../../../share/util/api/service';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';

/** メッセージ内の置き換え文字列定義 */
const USER = '$USER$';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  INPUT_DATA: 'inputData',
  WRITING: 'writing',
  SUCCESS: 'success',
  INVALID_DATA: 'invalidData',
  WRITE_ERROR: 'writeError',
  FAILED: 'failed',
};

export default {
  name: 'requirement140',
  data() {
    return {
      systemId: '',
      displayInfo: '',
      isLoading: true,
      phase: PHASE.START,
      twelveDigitModelCode: '',
      validateAppliedModel: undefined,
      validateOptionCode: undefined,
      chkValidateAppliedModel: false,
      chkValidateOptionCode: false,
      variantData: [],
      inputAppliedModel: '',
      inputOptionCode: '',
      rowIndex: 0,
      modal_requirement_140: false,
      lastEvaluatedKey: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.INVALID_DATA:
        case PHASE.WRITE_ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.INPUT_DATA:
        case PHASE.WRITING:
        default:
          return 'secondary';
      }
    },

    /** アプライドモデル・オプションコード入力画面での入力値 */
    inputData() {
      return [this.inputAppliedModel, this.inputOptionCode];
    }
  },

  watch: {
    inputData() {
      this.inputDataValidate();
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.validateAppliedModel = undefined;
      this.validateOptionCode = undefined;
      this.chkValidate = false;
      this.modal_requirement_140 = true;
      // ECUキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_140 = false;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** バリアントデータ取得 */
    async getVariantData() {
      try {
        this.isLoading = true;// バックエンドAPI GETのみ
        const response = await executeSyncRequest(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await apiService.getParamCall(
              apiService.WSP,
              '/variant/netApps/' + this.netAppId + '/systems/' + this.systemId,
              {
                last_evaluated_key: this.lastEvaluatedKey ? JSON.stringify(this.lastEvaluatedKey) : null
              }
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => { getApiResponse; return ''; },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // Backendでエラーが発生した場合、responseまたはresponse.dataはnullのためオプショナルチェーンで参照
        if (response?.data?.variant_data_list && response.data.variant_data_list.length !== 0) {
          // バリアントデータ取得
          this.variantData = this.variantData.concat(response.data.variant_data_list);
          // 12桁型式を取得
          this.twelveDigitModelCode = response.data.twelve_digit_model_code;

          if (response.data.last_evaluated_key) {
            // last_evaluated_key存在する場合、再度実行
            this.lastEvaluatedKey = response.data.last_evaluated_key;
            await this.getVariantData();
          } else if (this.twelveDigitModelCode) {
            // バリアントデータ全件取得完了
            // アプライドモデル・オプションコード初期値取得
            this.inputAppliedModel = this.twelveDigitModelCode.slice(0, 7);
            this.inputOptionCode = this.twelveDigitModelCode.slice(7).trim();
          }
        } else {
          // バリアントデータが取得できない場合の処理
          // 異常終了：汎用エラー
          this.showUnexpectedError();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * バリデーション(入力チェック)
     */
    inputDataValidate() {
      // バリデーションの実行
      this.chkValidateAppliedModel = this.checkValidateAppliedModel();
      this.chkValidateOptionCode = this.checkValidateOptionCode();
    },

    async validateWithVariantData() {
      if (!this.chkInputParam(this.inputAppliedModel, this.inputOptionCode, this.variantData)) {
        //無効なアプライドモデルとオプションコード
        this.phase = PHASE.INVALID_DATA;
        return;
      }
      //有効なアプライドモデルとオプションコード
      this.phase = PHASE.WRITING;
      await this.execute();
    },

    /**
     * アプライドモデル・オプションコード一致判定
     * @param inputAppliedModel アプライドモデルの入力値
     * @param inputOptionCode オプションコードの入力値
     * @param variantData 取得したバリアントデータ群
     * @returns 一致確認結果(true：一致/false：不一致)
     */
    chkInputParam(inputAppliedModel, inputOptionCode, variantData) {
      let idx = 0;
      let optionCode = "";
      let patternString = "";
      let regexPattern = "";
      let chkFlg = false;

      while (idx < variantData.length) {
        optionCode = variantData[idx].destination_code + variantData[idx].op_code;
        // アプライドモデルの入力値が取得したバリアントデータのアプライドモデルと一致すること
        if (inputAppliedModel.toUpperCase() === variantData[idx].applied_model.toUpperCase()) {
          // [_] を [""]に変換
          patternString = optionCode.replace(/_/g, "").toUpperCase();
          // [*] を [.?](任意の0文字または1文字)に変換
          regexPattern = new RegExp("^" + patternString.replace(/\*/g, ".?") + "$");
          // オプションコードの入力値が取得したバリアントデータの仕向値コード＋OPコードの連結文字列と一致すること
          if (regexPattern.test(inputOptionCode.toUpperCase())) {
            chkFlg = true;
            this.rowIndex = idx;
            break;
          }
        }
        idx++;
      }
      return chkFlg;
    },

    /**
     * アプライドモデルのバリデーションチェック
     * @returns バリデーション確認結果(true：バリデーションOK/false：バリデーションエラー)
     */
    checkValidateAppliedModel() {
      this.validateAppliedModel = undefined;

      // アプライドモデルが空の場合、エラーを出す
      if (!this.inputAppliedModel) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_required`) };
        return false;
      }

      // アプライドモデルの不正文字列の確認
      if (this.checkInvalidCharacter(this.inputAppliedModel)) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }

      // アプライドモデルの桁数チェック
      if (this.inputAppliedModel.length !== 7) {
        this.validateAppliedModel = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['7']) };
        return false;
      }
      return true;
    },

    /** オプションコードのバリデーションチェック
     * @returns バリデーション確認結果(true：バリデーションOK/false：バリデーションエラー)
     */
    checkValidateOptionCode() {
      this.validateOptionCode = undefined;

      // オプションコードの不正文字列の確認
      if (this.checkInvalidCharacter(this.inputOptionCode)) {
        this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      }

      // オプションコードはアプライドモデルの入力値によってバリデーションの内容が変わるため
      // アプライドモデルのバリデーションがエラーの場合は後続のチェックを実行しない
      if (!this.chkValidateAppliedModel) {
        return true;
      }

      // アプライドモデルの5桁目の値の取得
      const fifthChar = this.inputAppliedModel.slice(4, 5);
      const regExpNum = /^(\d+)$/i;

      // 5桁目が数字かどうかを判定
      if (regExpNum.test(fifthChar)) {
        // 数字の場合、オプションコードが3桁以下かのチェック
        if (this.inputOptionCode.length > 3) {
          this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['3']) };
          return false;
        }
      } else {
        // 英字の場合、オプションコードが4桁以下かのチェック
        if (this.inputOptionCode.length > 4) {
          this.validateOptionCode = { valid: true, msg: this.$t(`message.alert_validate_max_length`, ['4']) };
          return false;
        }
      }
      return true;
    },

    /**
     * 使用不可な文字列が含まれていないかの確認関数
     * @param code 対象code
     */
    checkInvalidCharacter(code) {
      // \x20から\x7eが制御文字を除く通常の半角英数字なので、それ以外の場合は弾く
      const halfReqExp = new RegExp(/^[a-zA-Z0-9]*$/);
      return !halfReqExp.test(code);
    },

    /** 保存処理 */
    async execute() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write',
            applied_model: this.variantData[this.rowIndex].applied_model,
            destination_code: this.variantData[this.rowIndex].destination_code,
            op_code: this.variantData[this.rowIndex].op_code,
            development_code: this.variantData[this.rowIndex].development_code,
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了
              switch (response.data.user_data.result) {
                case 'ok': {
                  let replaceMessage = '';
                  replaceMessage = this.$t(`${this.i18nWsKey}.REQ_WS_140_TXT_7`).replace(USER, this.variantData[this.rowIndex].applied_model);
                  replaceMessage = replaceMessage.replace(USER, this.variantData[this.rowIndex].destination_code + this.variantData[this.rowIndex].op_code);
                  this.displayInfo = replaceMessage;
                  // 停止処理
                  await this.stopProcess();
                  break;
                }
                case 'ng':
                  // 終了処理
                  await this.exitProcess(0);
                  // 書込み失敗表示画面へ遷移
                  this.phase = PHASE.WRITE_ERROR;
                  break;
              }
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 異常終了：共通エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** アプライドモデル・オプションコード入力画面へ遷移 */
    inputDataProcess() {
      // アプライドモデル・オプションコード入力表示画面へ遷移
      this.phase = PHASE.INPUT_DATA;
    },

    /** 停止処理 */
    async stopProcess() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 正常終了
              await this.exitProcess(0);
              // DefaultSession遷移の結果に関わらず停止画面へ遷移
              this.phase = PHASE.SUCCESS;
              break;
            case REQUEST_STATUS_FAILED:
            default:
              // 異常終了は共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 終了処理
     * @param {*} existence 0:停止：何もしない, 1:共通エラー：機能終了
     * @returns
     */
    async exitProcess(existence) {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 共通エラーの場合
        if (existence === 1) {

          // 機能終了
          this.closeWizard();
          return;

        }

        return;

      } finally {
        this.isLoading = false;
      }
    },
  },

  /** ライフサイクルイベント */
  async mounted() {
    this.init();
    await this.getVariantData();
  },
};